import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useConfig } from "../lib/ConfigContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, MapPin, ChevronRight, Save, X } from "lucide-react";

export default function AdminLocationsTab() {
  const [mains, setMains] = useState([]);
  const [subsByMain, setSubsByMain] = useState({});
  const [expanded, setExpanded] = useState({});
  const [newMain, setNewMain] = useState("");
  const [newSubFor, setNewSubFor] = useState({}); // { main_id: "draft" }
  const [editing, setEditing] = useState({}); // {main: id|null, sub: id|null}
  const [editValue, setEditValue] = useState("");
  const { refresh: refreshConfig } = useConfig();

  const load = async () => {
    try {
      const r = await api.get("/admin/main-areas");
      setMains(r.data || []);
      refreshConfig?.();
    } catch (e) {
      toast.error("Failed to load areas");
    }
  };
  useEffect(() => { load(); }, []);

  const loadSubs = async (mainId) => {
    try {
      const r = await api.get(`/admin/sub-areas?main_area_id=${mainId}`);
      setSubsByMain((s) => ({ ...s, [mainId]: r.data || [] }));
    } catch (e) {
      toast.error("Failed to load sub-areas");
    }
  };

  const toggleExpand = (id) => {
    setExpanded((e) => {
      const next = { ...e, [id]: !e[id] };
      if (next[id] && !subsByMain[id]) loadSubs(id);
      return next;
    });
  };

  const addMain = async () => {
    const name = newMain.trim();
    if (!name) return;
    try {
      await api.post("/admin/main-areas", { name });
      toast.success(`${name} added`);
      setNewMain("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const renameMain = async (id) => {
    const name = editValue.trim();
    if (!name) return;
    try {
      await api.patch(`/admin/main-areas/${id}`, { name });
      toast.success("Renamed");
      setEditing({});
      setEditValue("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteMain = async (m) => {
    if (!window.confirm(`Delete ${m.name}? All sub-areas under it will be removed and listings unlinked.`)) return;
    try {
      await api.delete(`/admin/main-areas/${m.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const addSub = async (mainId) => {
    const name = (newSubFor[mainId] || "").trim();
    if (!name) return;
    try {
      await api.post("/admin/sub-areas", { main_area_id: mainId, name });
      toast.success(`${name} added`);
      setNewSubFor((s) => ({ ...s, [mainId]: "" }));
      loadSubs(mainId);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const renameSub = async (sid, mainId) => {
    const name = editValue.trim();
    if (!name) return;
    try {
      await api.patch(`/admin/sub-areas/${sid}`, { name });
      toast.success("Renamed");
      setEditing({});
      setEditValue("");
      loadSubs(mainId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteSub = async (s, mainId) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await api.delete(`/admin/sub-areas/${s.id}`);
      toast.success("Deleted");
      loadSubs(mainId);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-locations-tab">
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <h3 className="font-heading font-semibold text-lg tracking-tight inline-flex items-center gap-2">
          <MapPin size={16} /> Hierarchical Locations
        </h3>
        <p className="text-[12px] text-[#71717A] mt-1">
          Structured Main Area → Sub Area catalog. Listings use these dropdowns instead of free text.
          Same sub-area name may exist under different main areas (e.g., "Sec 7" can be under both Dwarka and Rohini).
        </p>
        <div className="flex gap-2 mt-4">
          <Input value={newMain} onChange={(e) => setNewMain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMain())} placeholder="New main area, e.g., Janakpuri" className="rounded-sm max-w-xs" data-testid="loc-new-main-input" />
          <Button size="sm" className="btn-brand rounded-sm" onClick={addMain} data-testid="loc-new-main-add-btn">
            <Plus size={12} className="mr-1" /> Add main area
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="loc-tree">
        {mains.length === 0 && <div className="text-center py-10 text-[#A1A1AA]">No main areas yet.</div>}
        {mains.map((m) => (
          <div key={m.id} className="border-b border-[#F4F4F5] last:border-b-0" data-testid={`main-area-${m.id}`}>
            <div className="flex items-center gap-2 px-4 py-3 hover:bg-[#FAFAFA] cursor-pointer" onClick={() => toggleExpand(m.id)}>
              <ChevronRight size={14} className={`text-[#A1A1AA] transition-transform ${expanded[m.id] ? "rotate-90" : ""}`} />
              <MapPin size={13} className="text-[#0055FF]" />
              {editing.main === m.id ? (
                <div className="flex items-center gap-1.5 flex-1" onClick={(e) => e.stopPropagation()}>
                  <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameMain(m.id)} className="rounded-sm h-7 max-w-xs text-[13px]" autoFocus data-testid={`main-edit-input-${m.id}`} />
                  <Button size="sm" className="btn-brand rounded-sm h-7" onClick={() => renameMain(m.id)} data-testid={`main-edit-save-${m.id}`}>
                    <Save size={11} />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => { setEditing({}); setEditValue(""); }}>
                    <X size={11} />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="font-semibold text-[14px] text-[#09090B] flex-1" data-testid={`main-name-${m.id}`}>{m.name}</span>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] bg-[#F4F4F5] px-2 py-0.5 rounded-sm">
                    {m.sub_area_count} sub
                  </span>
                  <Button size="sm" variant="ghost" className="rounded-sm h-7" onClick={(e) => { e.stopPropagation(); setEditing({ main: m.id }); setEditValue(m.name); }} data-testid={`main-edit-btn-${m.id}`}>
                    <Edit3 size={11} />
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[#DC2626] hover:bg-[#FEE2E2]" onClick={(e) => { e.stopPropagation(); deleteMain(m); }} data-testid={`main-delete-btn-${m.id}`}>
                    <Trash2 size={11} />
                  </Button>
                </>
              )}
            </div>

            {expanded[m.id] && (
              <div className="bg-[#FAFAFA] border-t border-[#E4E4E7] p-3 pl-10" data-testid={`subs-${m.id}`}>
                <div className="space-y-1.5 mb-2">
                  {(subsByMain[m.id] || []).map((s) => (
                    <div key={s.id} className="flex items-center gap-2 bg-white border border-[#E4E4E7] rounded-sm px-3 py-1.5" data-testid={`sub-area-${s.id}`}>
                      <ChevronRight size={11} className="text-[#A1A1AA] rotate-90" />
                      {editing.sub === s.id ? (
                        <div className="flex items-center gap-1.5 flex-1">
                          <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameSub(s.id, m.id)} className="rounded-sm h-7 max-w-xs text-[13px]" autoFocus />
                          <Button size="sm" className="btn-brand rounded-sm h-7" onClick={() => renameSub(s.id, m.id)} data-testid={`sub-edit-save-${s.id}`}>
                            <Save size={11} />
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => { setEditing({}); setEditValue(""); }}>
                            <X size={11} />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-[13px] text-[#09090B] flex-1" data-testid={`sub-name-${s.id}`}>{s.name}</span>
                          <Button size="sm" variant="ghost" className="rounded-sm h-7" onClick={() => { setEditing({ sub: s.id }); setEditValue(s.name); }} data-testid={`sub-edit-btn-${s.id}`}>
                            <Edit3 size={11} />
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[#DC2626] hover:bg-[#FEE2E2]" onClick={() => deleteSub(s, m.id)} data-testid={`sub-delete-btn-${s.id}`}>
                            <Trash2 size={11} />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                  {(subsByMain[m.id] || []).length === 0 && <div className="text-[11px] text-[#A1A1AA] pl-2">No sub-areas yet.</div>}
                </div>
                <div className="flex gap-2 pl-2">
                  <Input
                    value={newSubFor[m.id] || ""}
                    onChange={(e) => setNewSubFor((s) => ({ ...s, [m.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSub(m.id))}
                    placeholder="New sub area, e.g., Om Vihar"
                    className="rounded-sm max-w-xs h-8 text-[12px]"
                    data-testid={`sub-new-input-${m.id}`}
                  />
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => addSub(m.id)} data-testid={`sub-new-btn-${m.id}`}>
                    <Plus size={11} className="mr-1" /> Add sub
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
